/**
 * Global image preload cache
 *
 * This module provides a mechanism to track which images have been preloaded
 * and are ready to display immediately without showing loading states.
 *
 * Images are tracked by their base URL (without query parameters like S3 signatures)
 * to handle presigned URLs that may change over time.
 */

// Cache of preloaded image base URLs
const preloadedImages = new Set<string>();

// Cache of images currently being preloaded (to avoid duplicate requests)
const pendingPreloads = new Map<string, Promise<boolean>>();

/**
 * Extracts the base URL without query parameters
 */
function getBaseUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return `${urlObj.origin}${urlObj.pathname}`;
  } catch {
    // If URL parsing fails, return the original URL
    return url.split('?')[0];
  }
}

/**
 * Check if an image has already been preloaded (by base URL)
 */
export function isImagePreloaded(url: string | undefined | null): boolean {
  if (!url?.trim()) return false;
  const baseUrl = getBaseUrl(url);
  return preloadedImages.has(baseUrl);
}

/**
 * Preload an image and mark it as ready in the cache
 * Returns a promise that resolves to true if successful, false if failed
 */
export function preloadImage(url: string): Promise<boolean> {
  const baseUrl = getBaseUrl(url);

  // Already preloaded
  if (preloadedImages.has(baseUrl)) {
    return Promise.resolve(true);
  }

  // Already preloading
  const pending = pendingPreloads.get(baseUrl);
  if (pending) {
    return pending;
  }

  // Start preloading
  const promise = new Promise<boolean>((resolve) => {
    const img = new Image();

    img.onload = () => {
      preloadedImages.add(baseUrl);
      pendingPreloads.delete(baseUrl);
      resolve(true);
    };

    img.onerror = () => {
      pendingPreloads.delete(baseUrl);
      resolve(false);
    };

    img.src = url;
  });

  pendingPreloads.set(baseUrl, promise);
  return promise;
}

/**
 * Mark an image as preloaded (used when an image loads naturally via <img> tag)
 */
export function markImageAsPreloaded(url: string | undefined | null): void {
  if (!url?.trim()) return;
  const baseUrl = getBaseUrl(url);
  preloadedImages.add(baseUrl);
}

/**
 * Preload multiple images in parallel
 * Returns when all images have attempted to load (success or failure)
 */
export async function preloadImages(urls: (string | undefined | null)[]): Promise<void> {
  const validUrls = urls.filter((url): url is string => Boolean(url?.trim()));
  await Promise.all(validUrls.map(preloadImage));
}

/**
 * Clear the preload cache (useful for testing or forced refresh)
 */
export function clearPreloadCache(): void {
  preloadedImages.clear();
  pendingPreloads.clear();
}
